import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";

interface ProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  project: {
    id: string;
    title: string;
    description: string | null;
    media_url: string | null;
    media_type: string | null;
  };
}

const ProjectModal = ({ isOpen, onClose, project }: ProjectModalProps) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const hasMedia = project.media_url && (project.media_type === 'image' || project.media_type?.startsWith('video'));

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl w-full p-0">
        <DialogTitle className="sr-only">{project.title}</DialogTitle>
        <div className="flex flex-col md:flex-row">
          {/* Media Display Section */}
          <div className="relative flex-1 bg-black min-h-[300px] md:min-h-[500px]">
            {hasMedia ? (
              project.media_type?.startsWith('video') ? (
                <video
                  src={project.media_url}
                  controls
                  autoPlay
                  className="w-full h-full object-contain"
                />
              ) : (
                <img
                  src={project.media_url}
                  alt={project.title}
                  className="w-full h-full object-contain"
                />
              )
            ) : (
              <div className="w-full h-full flex items-center justify-center text-white">
                No media available
              </div>
            )}
            
            {/* Only show navigation arrows for images */}
            {hasMedia && project.media_type === 'image' && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute left-2 top-1/2 -translate-y-1/2 text-white hover:bg-black/50"
                  onClick={() => setCurrentImageIndex((prev) => Math.max(0, prev - 1))}
                >
                  <ChevronLeft className="h-8 w-8" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-white hover:bg-black/50"
                  onClick={() => setCurrentImageIndex((prev) => prev + 1)}
                >
                  <ChevronRight className="h-8 w-8" />
                </Button>
              </>
            )}
          </div>

          {/* Project Info Section */}
          <div className="w-full md:w-1/3 p-6 bg-white">
            <h2 className="text-2xl font-bold mb-4">{project.title}</h2>
            {project.description && (
              <p className="text-gray-600 mb-6">{project.description}</p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectModal;